import { useMemo, useState } from 'react';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import {
  Card, Chip, DataTable, EmptyState, Pane, PaneHead, StatCard, type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePayrollRun } from '@/pages/roles/accountant/lib/useAccounts';
import { usePageMeta } from '@/hooks/usePageMeta';
import { MonthFilterRow } from '../components/MonthFilterRow';
import { Bar, DayBars, StackBar } from '../components/Charts';
import {
  dayLabel, daysOfMonth, doctorName, lastCompleteMonth, monthDay, monthLabel, monthName,
  monthRange, money, num, shiftMonth, weekdayDay,
  monthChip,
} from '../lib/scope';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { billedOf, splitByBranch, useAppointments, useBranches, useInvoices, useProviders } from '../lib/useManagerData';
import { ManagerSurface } from '../components/ManagerSurface';
import { BranchBars } from '../components/BranchBars';
import { ManagerMenu } from '../components/ManagerMenu';
import { PayoutDetail } from '../ledger/PayoutDetail';

/** How a bill was settled, in the board's order and colours. */
const METHODS: Array<{ key: string; label: string; colour: string }> = [
  { key: 'cash', label: 'Cash', colour: 'var(--brand-500)' },
  { key: 'card', label: 'Card', colour: 'var(--brand-300)' },
  { key: 'mobile_payment', label: 'InstaPay', colour: 'var(--info-600)' },
  { key: 'insurance', label: 'Insurance', colour: 'var(--beige-500)' },
  { key: 'bank_transfer', label: 'Transfer', colour: 'var(--ink-400)' },
];

/**
 * Clinic manager · Revenue & collections — `g_rev.dc.html`.
 *
 * COLLECTED IS MONEY THAT ARRIVED, BILLED IS MONEY THAT WAS ASKED FOR, and the
 * board spends a paragraph on the difference because a clinic that reads one
 * for the other thinks it is 16% richer than it is. `amountPaid` is collected;
 * `total` is billed; the gap is what patients and insurers still owe.
 *
 * THE PANE IS THE DOCTOR OF THE SELECTED ROW, never a second report — click
 * another row and it becomes that doctor's.
 *
 * WHAT THE BOARD HAS AND THIS DOES NOT: the share percentage and the payout it
 * feeds. There is no contract, share or payout model in the backend, so the
 * columns that would print them say so instead of printing a plausible number.
 */
/** The row for bills that are not tied to any doctor's visit. */
const UNATTRIBUTED = '__unattributed';

export function ManagerRevenuePage() {
  const narrow = useNarrowViewport();
  const [month, setMonth] = useState(lastCompleteMonth());
  const [compare, setCompare] = useState(shiftMonth(lastCompleteMonth(), -1));
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [payoutFor, setPayoutFor] = useState<{ id: string; name: string } | null>(null);

  const range = useMemo(() => monthRange(month), [month]);
  const prevRange = useMemo(() => monthRange(compare), [compare]);
  const invoices = useInvoices(range);
  const prevInvoices = useInvoices(prevRange);
  const appointments = useAppointments(range);
  const { providers } = useProviders();
  const branches = useBranches();
  const branchName = branches[0]?.name ?? 'This clinic';

  usePageMeta({ title: 'Revenue & collections' });

  const collected = invoices.page.rows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);
  const billed = invoices.page.rows.reduce((s, i) => s + billedOf(i), 0);
  const rate = billed ? Math.round((collected / billed) * 100) : 0;
  const prevCollected = prevInvoices.page.rows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);
  const trend = prevCollected ? Math.round(((collected - prevCollected) / prevCollected) * 100) : null;
  /* THE GAP IS BILLED − COLLECTED, to the pound, and the two halves it is split
     into are made to add up to it: the insurer's share is read off the bills
     paid by insurance and the patients' share is what is left. Summing each
     half's balances and rounding them one by one is how 24,975 + 861 came to
     sit under a gap of 26,086 − 250. */
  const gap = Math.round(billed) - Math.round(collected);
  const insurerOwed = Math.min(
    gap,
    Math.round(
      invoices.page.rows
        .filter((i) => i.status !== 'cancelled' && i.paymentMethod === 'insurance')
        .reduce((s, i) => s + Number(i.balance || 0), 0),
    ),
  );
  const patientOwed = gap - insurerOwed;
  /* Until the month's reads have answered, nothing here is 0 — it is not known. */
  const loading = invoices.isLoading || appointments.isLoading;

  /* The doctor behind a bill is the provider on the appointment it was raised
     against — `issuedBy` is whoever sat at the desk. */
  const providerOfAppointment = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of appointments.page.rows) if (a.providerId) m.set(a.id, a.providerId);
    return m;
  }, [appointments.page.rows]);

  interface DoctorMoney {
    id: string;
    name: string;
    collected: number;
    billed: number;
    method: Record<string, number>;
  }

  const byDoctor: DoctorMoney[] = useMemo(() => {
    const m = new Map<string, DoctorMoney>();
    for (const p of providers) {
      m.set(p.id, { id: p.id, name: doctorName(p), collected: 0, billed: 0, method: {} });
    }
    for (const inv of invoices.page.rows) {
      const pid = inv.appointmentId ? providerOfAppointment.get(inv.appointmentId) : undefined;
      if (!pid) continue;
      const row = m.get(pid);
      if (!row) continue;
      const paid = Number(inv.amountPaid || 0);
      row.collected += paid;
      row.billed += billedOf(inv);
      const key = inv.paymentMethod ?? 'unrecorded';
      row.method[key] = (row.method[key] ?? 0) + paid;
    }
    const rows = [...m.values()].filter((d) => d.collected > 0 || d.billed > 0)
      .sort((a, b) => b.collected - a.collected);
    /* BILLS NOT TIED TO A DOCTOR'S VISIT (a walk-in at the desk, a product sale)
       are money the clinic collected all the same. They get a row of their own
       so the doctors add up to the month instead of to a smaller number. */
    const loose = { id: UNATTRIBUTED, name: 'Not tied to a visit', collected: 0, billed: 0, method: {} as Record<string, number> };
    for (const inv of invoices.page.rows) {
      const pid = inv.appointmentId ? providerOfAppointment.get(inv.appointmentId) : undefined;
      if (pid && m.has(pid)) continue;
      const paid = Number(inv.amountPaid || 0);
      loose.collected += paid;
      loose.billed += billedOf(inv);
      const key = inv.paymentMethod ?? 'unrecorded';
      loose.method[key] = (loose.method[key] ?? 0) + paid;
    }
    return loose.collected > 0 || loose.billed > 0 ? [...rows, loose] : rows;
  }, [providers, invoices.page.rows, providerOfAppointment]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return byDoctor;
    return byDoctor.filter((d) => d.name.toLowerCase().includes(q));
  }, [byDoctor, query]);

  const selected = selectedId ? byDoctor.find((d) => d.id === selectedId) ?? null : null;

  /* Collected per calendar day of the month. */
  const perDay = useMemo(() => {
    const sums = new Map<string, number>();
    for (const inv of invoices.page.rows) {
      sums.set(inv.date, (sums.get(inv.date) ?? 0) + Number(inv.amountPaid || 0));
    }
    return daysOfMonth(month).map((d) => ({
      key: d, value: sums.get(d) ?? 0, label: `${dayLabel(d)} — ${money(sums.get(d) ?? 0)}`,
    }));
  }, [invoices.page.rows, month]);

  const methodSlices = useMemo(
    () => METHODS.map((m) => ({
      key: m.key, label: m.label, colour: m.colour,
      value: invoices.page.rows
        .filter((i) => i.paymentMethod === m.key)
        .reduce((s, i) => s + Number(i.amountPaid || 0), 0),
    })),
    [invoices.page.rows],
  );

  /* The month's payout run, so the share and net columns are the same
     figures the payroll screen shows rather than a second opinion. */
  const { data: run } = usePayrollRun(month);
  const runLineFor = (personId: string) =>
    run?.lines.find((l) => l.personId === personId) ?? null;

  const rows: TableRow[] = shown.map((d) => d.id === UNATTRIBUTED ? ({
    id: d.id,
    cells: {
      doctor: (
        <div>
          <p className="t-strong">{d.name}</p>
          <p className="t-cap mut">{branchName} · walk-in and other bills, no doctor behind them</p>
        </div>
      ),
      base: <span className="w6 num">{num(d.collected)}</span>,
      share: <span className="mut">—</span>,
      payout: <span className="mut">—</span>,
      state: <span className="t-cap mut" />,
    },
  }) : ({
    id: d.id,
    cells: {
      doctor: (
        <div>
          <p className="t-strong">{d.name}</p>
          <p className="t-cap mut">{branchName} · collected on their visits</p>
        </div>
      ),
      base: <span className="w6 num">{num(d.collected)}</span>,
      /* THE PAYOUT MODEL EXISTS NOW, so these read it. A person with no deal
         still shows "not set" — which is a fact about that person rather
         than about the product. */
      /* THE SHARE, NOT THE WHOLE SENTENCE. A 12%-wide column cannot hold
         "room rent — she pays · base 53318" without wrapping to three lines;
         the full deal reads on People we pay, which is where it belongs. */
      share: <span className={runLineFor(d.id) ? 'w6' : 'mut'}>{shareOf(runLineFor(d.id))}</span>,
      payout: (
        <span className="w6 num">
          {runLineFor(d.id) ? num(Math.abs(runLineFor(d.id)!.netMinor) / 100) : '—'}
        </span>
      ),
      state: (
        <span className="t-cap mut">
          {run?.status
            ? run.status === 'draft'
              ? 'draft run'
              : run.status === 'sent'
                ? 'awaiting approval'
                : run.status
            : 'no payout run'}
        </span>
      ),
    },
  }));

  const selectedLine = selected ? runLineFor(selected.id) : null;
  const paneSig = `${selected?.id ?? ''}:${selected?.collected ?? 0}:${selected?.billed ?? 0}:${selectedLine?.netMinor ?? ''}:${run?.status ?? ''}`;

  const crumb = usePhoneCrumb('Reports', `Revenue · ${monthChip(month).split(' ')[0]}`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Revenue & collections',
      sub: `${monthLabel(month)} · all branches · vs ${monthName(compare)}`,
      search: 'Search a doctor, a branch, a method…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(`revenue-${month}`, ['Doctor', 'Base collected', 'Billed'],
            byDoctor.map((d) => [d.name, Math.round(d.collected), Math.round(d.billed)])),
      },
      pane: selected ? (
        <Pane
          head={
            <PaneHead
              overline="The doctor of the selected row"
              title={`${selected.name} — ${monthLabel(month).split(' ')[0]}`}
              sub={`${branchName} · collected on their visits`}
            />
          }
          foot={
            <button type="button" className="btn btn-outline" style={{ flex: 1 }}
              onClick={() => setPayoutFor({ id: selected.id, name: selected.name })}>
              Payout detail ›
            </button>
          }
        >
          <Card>
            <p className="t-ovl mut">Collected — the base</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>{money(selected.collected)}</p>
            <p className="t-cap mut">what a percentage would be computed on</p>
          </Card>
          <Card>
            <p className="t-ovl mut">By method</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              {selected.collected > 0
                ? METHODS.filter((m) => (selected.method[m.key] ?? 0) > 0)
                  .map((m) => `${m.label.toLowerCase()} ${Math.round(((selected.method[m.key] ?? 0) / selected.collected) * 100)}%`)
                  .join(' · ') || 'not recorded on any bill'
                : 'nothing collected this month'}
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">The payout it feeds</p>
            {selectedLine ? (
              <>
                <p className="t-sec sec" style={{ marginTop: 4 }}>
                  {shareOf(selectedLine)} · {money(Math.abs(selectedLine.netMinor) / 100)}
                </p>
                <p className="t-cap mut">
                  {selectedLine.reverse ? 'they pay the clinic' : 'net, after deductions'} ·{' '}
                  {run?.status === 'sent' ? 'awaiting approval' : run?.status === 'draft' ? 'draft run' : run?.status}
                </p>
              </>
            ) : (
              <>
                <p className="t-sec mut" style={{ marginTop: 4 }}>no deal set</p>
                <p className="t-cap mut">set it on People we pay and it joins the run</p>
              </>
            )}
          </Card>
          <Card kind="accent">
            <p className="t-sec brand">
              The pane is the doctor of the selected row, never a second report. Click the other
              row and it becomes that doctor’s.
            </p>
          </Card>
        </Pane>
      ) : undefined,
    },
    [month, compare, query, paneSig, branchName, collected, billed, selectedId],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      {payoutFor ? (
        <PayoutDetail
          open
          onClose={() => setPayoutFor(null)}
          doctorId={payoutFor.id}
          doctorName={payoutFor.name}
          period={month}
          periodLabel={monthLabel(month)}
        />
      ) : null}
      <div className="dcontent">
        <MonthFilterRow
          month={month} onMonth={(m) => { setMonth(m); setSelectedId(null); }}
          compare={compare} onCompare={setCompare}
          branchLine="all branches"
        />

        <div className="kit-stats" style={{ display: 'flex', gap: 12, margin: '4px 0 20px' }}>
          <StatCard label="Collected" value={loading ? '…' : money(collected)} tone="success" />
          <StatCard label="Billed" value={loading ? '…' : num(billed)} />
          <StatCard label="Collection rate" value={loading ? '…' : `${rate}%`} />
          <StatCard
            label={`vs ${monthName(compare)}`}
            value={loading || prevInvoices.isLoading ? '…' : trend === null ? 'no prior month' : `${trend >= 0 ? '▲' : '▼'} ${Math.abs(trend)}%`}
            tone={trend === null ? undefined : trend >= 0 ? 'success' : 'warn'}
          />
        </div>

        {narrow ? (
          /* PHONE: the board reads bars, not payout cards — branch, doctor, then how it was paid. */
          <>
            {(() => {
              const split = splitByBranch(invoices.page.rows, branches);
              const bmax = Math.max(1, ...split.map((b) => b.collected));
              const docs = byDoctor.filter((d) => d.collected > 0 || d.billed > 0).sort((a, b) => b.collected - a.collected);
              const dmax = Math.max(1, ...docs.map((d) => d.collected));
              const paid = methodSlices.reduce((n, m) => n + m.value, 0);
              return (
                <>
                  {split.map((b) => (
                    <Bar key={b.id ?? 'clinic'} label={b.name} value={b.collected} max={bmax} display={num(b.collected)} />
                  ))}
                  <p className="t-sec mut" style={{ margin: '14px 0 6px' }}>By doctor</p>
                  {docs.length ? docs.map((d) => (
                    <Bar key={d.id} label={d.name} value={d.collected} max={dmax} display={num(d.collected)} />
                  )) : <p className="t-sec mut">{loading ? 'Counting the month…' : 'No money moved this month'}</p>}
                  <p className="t-sec mut" style={{ margin: '14px 0 6px' }}>By method</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {paid > 0
                      ? methodSlices.filter((m) => m.value > 0).map((m) => (
                        <Chip key={m.key} kind="outline" small>{m.label} {Math.round((m.value / paid) * 100)}%</Chip>
                      ))
                      : <span className="t-sec mut">nothing was settled this period</span>}
                  </div>
                </>
              );
            })()}
          </>
        ) : (
        <>
        <DataTable
          caption="Revenue by doctor"
          phoneLabels
          columns={[
            { key: 'doctor', label: 'Doctor — collected on their visits', width: 46 },
            { key: 'base', label: 'Base collected', width: 14 },
            { key: 'share', label: 'Share', width: 10 },
            { key: 'payout', label: 'Net payout', width: 14 },
            { key: 'state', label: monthLabel(month).split(' ')[0], width: 16 },
          ]}
          rows={rows}
          selectedId={selectedId}
          onSelect={(r) => { if (r.id !== UNATTRIBUTED) setSelectedId((cur) => (cur === r.id ? null : r.id)); }}
          empty={loading ? <p className="t-sec mut">Counting the month…</p> :
            <EmptyState
              title="No money moved this month"
              sub="A bill settled against a doctor’s visit puts them here, with the money it collected."
            />}
        />

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          Base is collected money only — cash collected plus claims PAID, never billed. Share and
          net payout come from this month&rsquo;s payout run, so they are the same figures People
          we pay shows. A doctor with no deal reads &ldquo;not set&rdquo;, which is a fact about
          them rather than about the clinic.
        </p>

        <div style={{ height: 8 }} />

        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <BranchBars invoices={invoices.page.rows} />
            <Card>
              <p className="t-ovl mut mb8">Collected per day</p>
              <DayBars
                bars={perDay}
                height={90}
                left={monthDay(range.start)}
                middle={peakLine(perDay)}
                right={monthDay(range.end)}
              />
            </Card>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">By method</p>
              {/* THE CAPTION NAMES WHAT IS ON THE BAR. The board's line —
                  "watching cash share fall as InstaPay rises is how the drawer
                  risk shrinks" — names a method this clinic has never taken a
                  bill through, and it was printed over an empty bar in the
                  zero state too. The observation is the point, so it is made
                  about the methods that ARE in the month. */}
              <StackBar
                slices={methodSlices}
                sub={
                  methodSlices.some((m) => m.value > 0)
                    ? `watching the cash share fall as ${
                      andList(methodSlices.filter((m) => m.key !== 'cash' && m.value > 0)
                        .map((m) => m.label))
                    } rise is how the drawer risk shrinks`
                    : 'the drawer risk is the cash share — nothing was settled this period'
                }
              />
            </Card>
            <Card>
              <p className="t-ovl mut mb8">Collected against billed</p>
              <Bar label="Billed" value={billed} max={billed || 1} display={num(billed)} tone="soft" />
              <Bar label="Collected" value={collected} max={billed || 1} display={num(collected)} />
              <p className="t-cap mut">
                {rate}% — the gap is what patients and insurers still owe: {num(patientOwed)} +{' '}
                {num(insurerOwed)} = {num(gap)} outstanding
              </p>
            </Card>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

/** "Card, Insurance and Transfer" — a list a person would read out. */
function andList(items: string[]): string {
  if (!items.length) return 'the other methods';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** "peak Thu 28 · quiet days dark" — the middle caption under the day bars. */
function peakLine(bars: Array<{ key: string; value: number }>): string {
  const peak = bars.reduce<{ key: string; value: number } | null>(
    (p, b) => (!p || b.value > p.value ? b : p), null);
  if (!peak || peak.value <= 0) return 'nothing collected this month';
  const dark = bars.filter((b) => b.value === 0).length;
  /* The WEEKDAY of the peak, like the board — which day of the week the clinic
     is busiest is the thing a manager staffs around; the date is not. */
  return `peak ${weekdayDay(peak.key)}${dark ? ` · ${dark} days dark` : ''}`;
}

/** "65%" for a share deal, the kind's short name for the other five. */
function shareOf(line: { dealKind: string; dealSummary: string | null } | null): string {
  if (!line) return 'not set';
  const pct = /([\d.]+)%/.exec(line.dealSummary ?? '');
  if (pct) return `${pct[1]}%`;
  const SHORT: Record<string, string> = {
    salary: 'salary',
    per_visit: 'per visit',
    room_rent: 'room rent',
    commission: 'commission',
    hybrid: 'hybrid',
    percent: 'not set',
  };
  return SHORT[line.dealKind] ?? line.dealKind;
}
